import React, { useEffect, useState } from 'react';
import { Radio } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import DateField from 'components/Form/dateField';
import EssFullWidthModal from 'components/modelpopup/baseModal';
import { useUnsavedChangesGuard } from 'context/unsavedChangesContext';
import {
  createPurchaseOrderApi,
  updatePurchaseOrderApi,
  confirmPurchaseOrderApi,
  getUniformItemsApi,
  getPurchaseOrderDocumentApi,
  uniformErrorMessage,
  flattenErrorMessages,
} from 'api/services/uniformManagement';
import DocumentAttachment from '../../components/DocumentAttachment';
import {
  buildLinesForAllItems, buildLinesForItem, keepFilledLines, DEFAULT_LINE_QUANTITY,
} from './lineHelpers';

const schema = Yup.object({
  po_number: Yup.string().trim().required('PO / LPO number is required'),
  po_date: Yup.string().required('PO date is required'),
  expected_delivery_date: Yup.string().nullable(),
  supplier_name: Yup.string(),
  remarks: Yup.string(),
  status: Yup.string().oneOf(['DRAFT', 'OPEN']).required(),
  lines: Yup.array().of(Yup.object({
    uniform_item: Yup.object().nullable().required('Item is required'),
    size: Yup.object().nullable().when('uniform_item', {
      // Must match the active-size filter the Size FilterSelect's options are built
      // from below, or an item with only inactive sizes would show a disabled,
      // empty Size field that validation still demands a value for.
      is: (item) => (item?.sizes || []).filter((s) => s.status !== false).length > 0,
      then: (s) => s.required('Size is required'),
      otherwise: (s) => s.notRequired(),
    }),
    quantity: Yup.number().typeError('Enter a quantity').integer().min(1, 'Min 1').required(),
  })).min(1, 'Add at least one line')
    // The backend rejects a PO whose lines repeat an (item, size) pair — see
    // validate_item_lines. Catching it here means the user is told which row is
    // the duplicate instead of losing a round trip to a 400. A size-less item
    // keys on "none", matching how the server treats a null size.
    .test('no-duplicate-lines', 'Duplicate lines', function (value) {
      const firstSeenAt = new Map();
      const duplicates = [];
      (value || []).forEach((line, index) => {
        const itemId = line?.uniform_item?.value;
        // Rows with no item yet are incomplete, not duplicates — their own
        // required rule reports them.
        if (itemId === undefined || itemId === null) return;
        const key = `${itemId}::${line?.size?.value ?? 'none'}`;
        if (firstSeenAt.has(key)) duplicates.push({ index, firstLine: firstSeenAt.get(key) + 1 });
        else firstSeenAt.set(key, index);
      });
      if (!duplicates.length) return true;
      return new Yup.ValidationError(
        duplicates.map(({ index, firstLine }) => this.createError({
          path: `${this.path}.${index}.uniform_item`,
          message: `Same item and size as line ${firstLine}. Merge the quantities or choose a different size.`,
        })),
      );
    }),
});

const emptyLine = { uniform_item: null, size: null, quantity: 1 };

const defaultValues = {
  po_number: '',
  po_date: '',
  expected_delivery_date: '',
  supplier_name: '',
  remarks: '',
  status: 'DRAFT',
  invoice_number: '',
  invoice_date: '',
  lines: [emptyLine],
};

const toastOpts = { position: 'top-right', autoClose: 3000, transition: Bounce };

/** Create / edit modal for a Purchase Order. Edit mode (po supplied) is only
 *  wired for DRAFT purchase orders by the caller (PODetail).
 *
 *  `prefill` seeds a NEW purchase order's lines without making it an edit:
 *  `{ lines: [{ uniform_item, uniform_item_name, size, size_name, size_code,
 *  ordered_quantity }] }`, the same line shape a saved PO carries, so the
 *  reorder path from the replacement report and the edit path share one
 *  mapping. Header fields start blank as on any new PO. */
const POFormModal = ({ open, onClose, onSaved, po, prefill }) => {
  const isEdit = !!po;
  // What the form is seeded from on open: the PO being edited, or the
  // caller's prefill for a new one.
  const seed = po || prefill;

  const [saving, setSaving] = useState(false);
  const [uniformItems, setUniformItems] = useState([]);
  const [uniformItemsLoading, setUniformItemsLoading] = useState(false);
  // Tracks whether the items fetch has settled (success or failure) at least once,
  // so the edit-population effect below knows when it's safe to resolve each line's
  // full size list instead of populating too early with an empty uniformItems list.
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [poDoc, setPoDoc] = useState(null);
  const [invoiceDoc, setInvoiceDoc] = useState(null);

  // The two attachments live outside react-hook-form, so `formState.isDirty`
  // cannot see them: a user who uploaded a PO document and typed nothing else
  // would look clean. Compared by value against what the record arrived with —
  // the same baseline the open/reset effect seeds them from — rather than by a
  // "was touched" flag, so uploading a document and then removing it again
  // correctly reads as clean.
  const baselinePoDocUrl = po?.po_document_url || '';
  const baselineInvoiceDocUrl = po?.invoice_document_url || '';
  const docsDirty = (poDoc?.document_url || '') !== baselinePoDocUrl
    || (invoiceDoc?.document_url || '') !== baselineInvoiceDocUrl;

  const {
    control, handleSubmit, formState: { errors, isDirty }, reset, watch, getValues, setValue, setError,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { fields, append, remove, replace } = useFieldArray({ control, name: 'lines' });
  const [bulkQty, setBulkQty] = useState(DEFAULT_LINE_QUANTITY);

  // Bulk line actions. Each appends only (item, size) pairs not already present,
  // so none of them can produce the duplicate rows the form and server reject.
  // Blank starter rows are dropped rather than left stranded above the new ones.
  const appendLines = (generated) => {
    if (!generated.length) {
      toast.info('Every item and size is already on this order', toastOpts);
      return;
    }
    const kept = keepFilledLines(getValues('lines'));
    replace([...kept, ...generated]);
  };

  const addAllItems = () => appendLines(buildLinesForAllItems(uniformItems, getValues('lines'), bulkQty));

  const addAllSizesForRow = (index) => {
    const line = (getValues('lines') || [])[index];
    const item = line?.uniform_item;
    if (!item) return;
    // Look the item up in the master list: a row's own copy may predate the size
    // patch that runs once the item list loads.
    const fullItem = uniformItems.find((u) => u.value === item.value) || item;
    appendLines(buildLinesForItem(fullItem, getValues('lines'), bulkQty));
  };

  const clearLines = () => {
    replace([emptyLine]);
    toast.info('Lines cleared', toastOpts);
  };

  const applyQuantityToAll = () => {
    const qty = Number(bulkQty);
    if (!Number.isInteger(qty) || qty < 1) {
      toast.error('Enter a whole quantity of at least 1', toastOpts);
      return;
    }
    (getValues('lines') || []).forEach((_, index) => setValue(`lines.${index}.quantity`, qty));
  };
  const status = watch('status');
  const lines = watch('lines');

  useEffect(() => {
    if (!open) return;
    setUniformItemsLoading(true);
    getUniformItemsApi({ page_size: 1000 }).then((response) => {
      setUniformItems(
        (response.data?.results || [])
          // Backend's validate_item_lines requires status=True on the uniform item;
          // mirror that here (like the `s.status !== false` size filter below) so the
          // dropdown never offers a deactivated item the submit would then reject.
          .filter((i) => i.status !== false)
          .map((i) => ({ label: i.name, value: i.id, sizes: i.sizes || [] }))
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      );
    }).catch((e) => {
      toast.error(uniformErrorMessage(e, 'Failed to load uniform items'), toastOpts);
    }).finally(() => {
      setUniformItemsLoading(false);
      setItemsLoaded(true);
    });
  }, [open]);

  // Populate the form when opening for edit, or reset for a fresh create. This runs
  // synchronously on open — it does NOT wait on the uniform items master list (which
  // may still be in flight), so the modal always renders the PO's saved data on first
  // paint instead of sitting blank. A line's `uniform_item.sizes` may start as `[]`
  // here if items haven't loaded yet; the effect below patches those in once they
  // have, without touching anything the user may have typed in the meantime.
  useEffect(() => {
    if (!open) return;
    if (seed) {
      const mappedLines = (seed.lines || []).map((l) => {
        const fullItem = uniformItems.find((u) => u.value === l.uniform_item);
        return {
          uniform_item: {
            label: l.uniform_item_name,
            value: l.uniform_item,
            sizes: fullItem?.sizes || [],
          },
          size: l.size ? { label: `${l.size_name} (${l.size_code})`, value: l.size } : null,
          quantity: l.ordered_quantity,
        };
      });
      reset({
        po_number: seed.po_number || '',
        po_date: seed.po_date || '',
        expected_delivery_date: seed.expected_delivery_date || '',
        supplier_name: seed.supplier_name || '',
        remarks: seed.remarks || '',
        status: seed.status === 'OPEN' ? 'OPEN' : 'DRAFT',
        invoice_number: seed.invoice_number || '',
        invoice_date: seed.invoice_date || '',
        lines: mappedLines.length ? mappedLines : [emptyLine],
      });
      setPoDoc(seed.po_document_url ? { document_url: seed.po_document_url, document_name: seed.po_document_name } : null);
      setInvoiceDoc(seed.invoice_document_url ? { document_url: seed.invoice_document_url, document_name: seed.invoice_document_name } : null);
    } else {
      reset(defaultValues);
      setPoDoc(null);
      setInvoiceDoc(null);
    }
    // Deliberately excludes `uniformItems`/`itemsLoaded`: this effect must populate
    // the form exactly once per open (or when the record being edited changes), not
    // re-run (and wipe in-progress edits via reset()) whenever the items master list
    // finishes loading — that's handled separately below via setValue, not reset().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seed, reset]);

  // Once the uniform items master list has loaded, patch each line's full size list
  // in via setValue — never reset() here. This preserves the header fields and any
  // in-progress edits (including a size the user already picked) untouched; it only
  // corrects `uniform_item.sizes` so the Size FilterSelect gets its real options.
  useEffect(() => {
    if (!open || !seed || !itemsLoaded || !uniformItems.length) return;
    const currentLines = getValues('lines') || [];
    currentLines.forEach((line, index) => {
      if (!line?.uniform_item) return; // an "Add Line" row with no item picked yet
      const fullItem = uniformItems.find((u) => u.value === line.uniform_item.value);
      if (!fullItem || line.uniform_item.sizes === fullItem.sizes) return; // not found, or already patched
      setValue(`lines.${index}.uniform_item`, { ...line.uniform_item, sizes: fullItem.sizes });
    });
    // getValues/setValue are stable references from react-hook-form; including them
    // is safe and doesn't cause extra runs. setValue here never changes `open`, `po`,
    // `itemsLoaded`, or the `uniformItems` array reference, so this effect cannot loop.
  }, [open, seed, itemsLoaded, uniformItems, getValues, setValue]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  // Dirty = RHF's own value comparison against the defaults the open/reset
  // effect installed, plus the two attachments it cannot see. Value comparison,
  // not touch tracking, throughout: a user who types a PO number and then
  // clears it again has nothing left to lose, and `reset()` on open makes the
  // *edited* PO's saved values the baseline, so simply opening a draft to look
  // at it is clean. The programmatic `setValue` that patches each line's size
  // options once the items master list lands does not dirty the form either —
  // RHF's setValue defaults to `shouldDirty: false`.
  //
  // `requestClose` drives the panel's X button and Esc, so those go through the
  // same prompt a sidebar click does instead of discarding silently.
  const { requestClose } = useUnsavedChangesGuard({
    active: open,
    isDirty: isDirty || docsDirty,
    isBusy: saving,
    onDiscard: handleClose,
    label: isEdit ? `Edit PO ${po.po_number}` : 'New Purchase Order',
  });

  // Shared PATCH/POST failure handling: map a field-map error onto the form via
  // setError, and always surface the toast text through uniformErrorMessage.
  const handleSaveError = (e, fallback = 'Failed to save purchase order') => {
    setSaving(false);
    const errorObj = e.response?.data?.error;
    if (errorObj && typeof errorObj === 'object' && !Array.isArray(errorObj)) {
      Object.entries(errorObj).forEach(([k, v]) => {
        // Flatten rather than String(v): a nested branch such as
        // {lines: {lines: ["Line 6: duplicate item/size line."]}} would
        // otherwise render as "[object Object]" under the Lines heading.
        const message = flattenErrorMessages(v, k).join(' ');
        if (message) setError(k, { type: 'manual', message });
      });
    }
    toast.error(uniformErrorMessage(e, fallback), toastOpts);
  };

  const onSubmit = (values) => {
    setSaving(true);
    const payload = {
      ...values,
      expected_delivery_date: values.expected_delivery_date || null,
      invoice_date: values.invoice_date || null,
      po_document_url: poDoc?.document_url || '',
      po_document_name: poDoc?.document_name || '',
      invoice_document_url: invoiceDoc?.document_url || '',
      invoice_document_name: invoiceDoc?.document_name || '',
      lines: values.lines.map((l) => ({
        uniform_item: l.uniform_item.value,
        size: l.size?.value ?? null,
        quantity: Number(l.quantity),
      })),
    };

    if (!isEdit) {
      // Create: the backend's serializer honours `status` directly (DRAFT or OPEN).
      createPurchaseOrderApi(payload).then((response) => {
        setSaving(false);
        toast.success('Purchase order created', toastOpts);
        onSaved(response.data?.data);
      }).catch((e) => handleSaveError(e));
      return;
    }

    // Edit: PurchaseOrderView.patch only applies a fixed set of header/document
    // fields — `status` isn't one of them, so it's silently ignored if sent. Strip
    // it from the PATCH and, if the user asked to move DRAFT -> OPEN, follow up
    // with the dedicated confirm endpoint once the header PATCH has succeeded.
    const wantsConfirm = po.status === 'DRAFT' && values.status === 'OPEN';
    const { status: _status, ...patchPayload } = payload;

    updatePurchaseOrderApi(po.id, patchPayload).then((response) => {
      if (!wantsConfirm) {
        setSaving(false);
        toast.success('Purchase order updated', toastOpts);
        onSaved(response.data?.data);
        return;
      }
      confirmPurchaseOrderApi(po.id).then((confirmResponse) => {
        setSaving(false);
        toast.success('Purchase order updated and confirmed', toastOpts);
        onSaved(confirmResponse.data?.data || response.data?.data);
      }).catch((e) => {
        // The header/line PATCH already succeeded — only the confirm step failed.
        // Refresh the caller with what was saved, but do not claim success.
        setSaving(false);
        toast.error(uniformErrorMessage(e, 'Purchase order was saved, but confirming it failed'), toastOpts);
        onSaved(response.data?.data);
      });
    }).catch((e) => handleSaveError(e));
  };

  return (
    <EssFullWidthModal mask={false} sidebarNavigable title={isEdit ? `Edit PO ${po.po_number}` : 'New Purchase Order'}
      open={open} onCancel={requestClose} className="full-width-modal">
      <div className="modal-content">
        <div className="modal-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-12 col-sm-6 col-xl-4">
                <div className="input-block mb-3">
                  <label className="col-form-label">PO / LPO Number <span className="text-danger">*</span></label>
                  <Controller name="po_number" control={control}
                    render={({ field }) => <input className="form-control" {...field} placeholder="PO number" />} />
                  {errors.po_number && <small className="text-danger">{errors.po_number.message}</small>}
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-4">
                <div className="input-block mb-3">
                  <label className="col-form-label" htmlFor="po-date">PO Date <span className="text-danger">*</span></label>
                  <Controller name="po_date" control={control}
                    render={({ field }) => (
                      <DateField id="po-date" ref={field.ref} value={field.value} onChange={field.onChange}
                        onBlur={field.onBlur} name={field.name} placeholder="Select PO date"
                        status={errors.po_date ? 'error' : undefined} />
                    )} />
                  {errors.po_date && <small className="text-danger">{errors.po_date.message}</small>}
                </div>
              </div>
              <div className="col-12 col-sm-6 col-xl-4">
                <div className="input-block mb-3">
                  <label className="col-form-label" htmlFor="po-expected-delivery-date">Expected Delivery Date</label>
                  <Controller name="expected_delivery_date" control={control}
                    render={({ field }) => (
                      <DateField id="po-expected-delivery-date" ref={field.ref} value={field.value} onChange={field.onChange}
                        onBlur={field.onBlur} name={field.name} placeholder="Select delivery date" />
                    )} />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-sm-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Supplier</label>
                  <Controller name="supplier_name" control={control}
                    render={({ field }) => <input className="form-control" {...field} placeholder="Supplier name" />} />
                </div>
              </div>
              <div className="col-12 col-sm-6">
                <div className="input-block mb-3">
                  <label className="col-form-label">Remarks</label>
                  <Controller name="remarks" control={control}
                    render={({ field }) => <input className="form-control" {...field} placeholder="Remarks" />} />
                </div>
              </div>
            </div>

            <hr />
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
              <h5 className="mb-0" style={{ fontSize: 15 }}>Lines</h5>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-1">
                  <input type="number" min="1" className="form-control form-control-sm" style={{ width: 80 }}
                    value={bulkQty} onChange={(e) => setBulkQty(e.target.value)}
                    aria-label="Quantity for bulk actions" />
                  <Button type="button" label="Apply to all" className="btn btn-outline-secondary btn-sm"
                    title="Set this quantity on every line"
                    onClick={applyQuantityToAll} />
                </div>
                <Button type="button" label="Add all items" icon="fa fa-layer-group"
                  className="btn btn-outline-primary btn-sm"
                  disabled={uniformItemsLoading || !uniformItems.length}
                  title="Add a line for every item and each of its sizes"
                  onClick={addAllItems} />
                <Button type="button" label="Clear lines" icon="fa fa-eraser"
                  className="btn btn-outline-secondary btn-sm"
                  title="Remove every line and start again"
                  onClick={clearLines} />
                <Button type="button" label="Add Line" icon="fa fa-plus" className="btn btn-outline-primary btn-sm"
                  onClick={() => append(emptyLine)} />
              </div>
            </div>
            {errors.lines?.message && <small className="text-danger d-block mb-2">{errors.lines.message}</small>}

            <div className="table-responsive mb-3">
              <table className="table table-bordered align-middle">
                <thead>
                  <tr>
                    {/* Item yields (220 -> 180) and Size does not: both hold a
                        picker, but Size is the column the user reports losing,
                        and an item name is legible truncated where a size code
                        picker at 140px is not. 180+180+90+44 = 494px of floor,
                        down from 568, which is what brings the grid inside a
                        tablet-width panel without horizontal scrolling. */}
                    <th style={{ minWidth: 180 }}>Item</th>
                    <th style={{ minWidth: 180 }}>Size</th>
                    <th style={{ minWidth: 90 }}>Quantity</th>
                    <th style={{ width: 44 }}> </th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => {
                    const selectedItem = lines?.[index]?.uniform_item;
                    const sizeOptions = (selectedItem?.sizes || [])
                      .filter((s) => s.status !== false)
                      .map((s) => ({ label: `${s.name} (${s.code})`, value: s.id }));
                    const sizeRequired = sizeOptions.length > 0;
                    const lineErrors = errors.lines?.[index];
                    return (
                      <tr key={field.id}>
                        <td>
                          <Controller name={`lines.${index}.uniform_item`} control={control}
                            render={({ field: { value, onChange } }) => (
                              <FilterSelect isClearable
                                onChange={(v) => { onChange(v); setValue(`lines.${index}.size`, null); }}
                                optionLabel="label" optionValue="value"
                                options={uniformItems} value={value} placeholder="Select Item"
                                isLoading={uniformItemsLoading} />
                            )} />
                          {lineErrors?.uniform_item && <small className="text-danger">{lineErrors.uniform_item.message}</small>}
                        </td>
                        <td>
                          <Controller name={`lines.${index}.size`} control={control}
                            render={({ field: { value, onChange } }) => (
                              <FilterSelect isClearable onChange={onChange} optionLabel="label" optionValue="value"
                                options={sizeOptions} value={value}
                                placeholder={!selectedItem ? 'Select item first' : sizeRequired ? 'Select Size' : 'No sizes'}
                                isDisabled={!selectedItem || !sizeRequired} isLoading={false} />
                            )} />
                          {lineErrors?.size && <small className="text-danger">{lineErrors.size.message}</small>}
                        </td>
                        <td>
                          <Controller name={`lines.${index}.quantity`} control={control}
                            render={({ field }) => <input className="form-control" {...field} type="number" min="1" />} />
                          {lineErrors?.quantity && <small className="text-danger">{lineErrors.quantity.message}</small>}
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            <button type="button" className="btn btn-link p-0"
                              title="Add a line for every other size of this item"
                              disabled={!selectedItem} onClick={() => addAllSizesForRow(index)}>
                              <i className="fa fa-arrows-split-up-and-left" aria-hidden="true" />
                              <span className="visually-hidden">Add all sizes for this item</span>
                            </button>
                            <button type="button" className="btn btn-link text-danger p-0" title="Remove line"
                              disabled={fields.length === 1} onClick={() => remove(index)}>
                              <i className="fa fa-trash" aria-hidden="true" />
                              <span className="visually-hidden">Remove line</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <hr />
            <div className="row">
              <div className="col-12 col-xl-6">
                <DocumentAttachment label="PO / LPO Document" value={poDoc} onChange={setPoDoc}
                  fetchDocumentApi={isEdit ? (p) => getPurchaseOrderDocumentApi(po.id, 'po', p) : undefined} />
              </div>
              <div className="col-12 col-xl-6">
                <DocumentAttachment label="Invoice Document" value={invoiceDoc} onChange={setInvoiceDoc}
                  fetchDocumentApi={isEdit ? (p) => getPurchaseOrderDocumentApi(po.id, 'invoice', p) : undefined} />
                <div className="row mt-2">
                  <div className="col-12 col-sm-6">
                    <label className="col-form-label">Invoice Number</label>
                    <Controller name="invoice_number" control={control}
                      render={({ field }) => <input className="form-control" {...field} placeholder="Invoice number" />} />
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="col-form-label" htmlFor="po-invoice-date">Invoice Date</label>
                    <Controller name="invoice_date" control={control}
                      render={({ field }) => (
                        <DateField id="po-invoice-date" ref={field.ref} value={field.value} onChange={field.onChange}
                          onBlur={field.onBlur} name={field.name} placeholder="Select invoice date" />
                      )} />
                  </div>
                </div>
              </div>
            </div>

            <hr />
            <div className="input-block mb-3">
              <label className="col-form-label d-block">Status</label>
              <Controller name="status" control={control}
                render={({ field: { value, onChange } }) => (
                  <Radio.Group value={value} onChange={(e) => onChange(e.target.value)}
                    optionType="button" buttonStyle="solid">
                    <Radio.Button value="DRAFT">Save as Draft</Radio.Button>
                    <Radio.Button value="OPEN">Confirm Order</Radio.Button>
                  </Radio.Group>
                )} />
            </div>

            <div className="submit-section">
              <Button loading={saving} disabled={saving} className="btn btn-primary submit-btn"
                type="submit" label={status === 'OPEN' ? 'Confirm Order' : 'Save Draft'} />
            </div>
          </form>
        </div>
      </div>
    </EssFullWidthModal>
  );
};

export default POFormModal;
